import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Ticket, Award, Crown, Gem, Sparkles, AlertCircle, Star, Building2, Landmark, ArrowRight, MessageCircle } from 'lucide-react';
export const TicketsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const ticketOptions = [{
    title: 'Darbar Pass',
    subtitle: 'Standing',
    features: ['Standing-room access to immersive experience', 'Late entry', 'Access to chai & sweets lounge', 'Photo moments throughout the venue', 'Digital souvenir collection'],
    limitations: ['Standing-room only', 'No reserved viewing area'],
    popular: false,
    icon: <Ticket className="w-8 h-8" data-id="element-1738" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Council',
    subtitle: 'Full Experience',
    features: ['Admission to immersive experience', 'Curated drinks & scent journey', 'Digital souvenir collection', 'Guided historical journey', 'VIP sensory experience', 'Behind-the-scenes tour'],
    popular: true,
    icon: <Award className="w-8 h-8" data-id="element-1739" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Founder Circle',
    subtitle: 'Limited Availability',
    features: ['All Council features', <span key="turban" data-id="element-1740">
          <strong data-id="element-1741">Personalized royal turban experience</strong> — wear an
          authentic turban designed and inspired by the Sikh Kingdom era
        </span>, <span key="coin" data-id="element-1742">
          <strong data-id="element-1743">Exclusive Maharaja Ranjit Singh Coin</strong>
        </span>, <span key="book" data-id="element-1744">
          <strong data-id="element-1745">Autographed pre-release copy of House of Lions</strong> —
          personally signed by author A.K. Singh
        </span>, 'Early prototype invite', 'Recognition as Founding Member', 'Private meet with creators', 'Custom historical portrait'],
    popular: false,
    icon: <Crown className="w-8 h-8" data-id="element-1746" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }, {
    title: 'Patron Circle',
    subtitle: 'Table for 10',
    features: ['All Founder Circle features', 'Private hosted table for 10 guests', <span key="likeness-patron" data-id="element-1747">
          <strong data-id="element-1748">Your likeness featured in the exhibit</strong>
        </span>, 'Lifetime recognition as a major supporter', 'Exclusive pre-event reception', 'Input into future cultural experiences'],
    popular: false,
    icon: <Gem className="w-8 h-8" data-id="element-1749" />,
    link: 'https://www.tickettailor.com/events/sikhkingdoms/1934316'
  }];
  return <motion.section id="tickets" ref={sectionRef} className="py-20 md:py-28 bg-black relative overflow-hidden" style={{
    opacity
  }} data-id="element-1750">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1751"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1752">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1753">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1754">
            Reserve Your Place
          </p>
          <h2 className="font-serif text-5xl md:text-7xl text-white mb-8" data-id="element-1755">
            TICKET
            <br data-id="element-1756" />
            <span className="text-amber-400" data-id="element-1757">TIERS</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1758"></div>
          <div className="max-w-3xl mx-auto" data-id="element-1759">
            <p className="text-xl text-amber-400 font-light mb-4" data-id="element-1760">
              Prototype Event · Summer 2026
            </p>
            <p className="text-white/70 font-light leading-relaxed mb-6" data-id="element-1761">
              Your ticket includes admission to the exclusive prototype event
              plus one monthly experience of your choice between May and
              December 2026.
            </p>
            <div className="flex justify-between items-center pt-4 border-t border-white/10" data-id="element-1762">
              <span className="text-amber-400 font-light tracking-wider" data-id="element-1763">
                Summer 2026
              </span>
              <span className="text-white/60 text-sm tracking-wider uppercase" data-id="element-1764">
                Prototype Event
              </span>
            </div>
          </div>
        </motion.div>
        {/* Tickets Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mb-20" data-id="element-1765">
          {ticketOptions.map((ticket, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: index * 0.1
        }} className={`relative ${ticket.popular ? 'lg:-mt-4 lg:mb-4' : ''}`} data-id="element-1766">
              {/* Popular Badge */}
              {ticket.popular && <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-1767">
                  <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2 border-2 border-amber-300" data-id="element-1768">
                    <Star className="w-4 h-4" fill="currentColor" data-id="element-1769" />
                    <span className="text-xs uppercase tracking-wider" data-id="element-1770">
                      Most Popular
                    </span>
                  </div>
                </div>}
              {/* Card */}
              <div className="border-2 border-amber-400/30 bg-black/80 h-full flex flex-col" data-id="element-1771">
                <div className={`p-8 flex flex-col h-full ${ticket.popular ? 'pt-12' : ''}`} data-id="element-1772">
                  {/* Icon & Title */}
                  <div className="text-center mb-8" data-id="element-1773">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-amber-400/30 bg-black" data-id="element-1774">
                      <div className="text-amber-400" data-id="element-1775">{ticket.icon}</div>
                    </div>
                    <h3 className="font-serif text-2xl text-white mb-2" data-id="element-1776">
                      {ticket.title}
                    </h3>
                    {ticket.subtitle && <p className="text-amber-400/60 text-sm font-light tracking-wider uppercase" data-id="element-1777">
                        {ticket.subtitle}
                      </p>}
                    <div className="w-16 h-px bg-amber-400/30 mx-auto mt-4" data-id="element-1778"></div>
                  </div>
                  {/* Features */}
                  <ul className="mb-8 flex-grow space-y-3" data-id="element-1779">
                    {ticket.features.map((feature, i) => <li key={i} className="text-white/70 text-sm flex items-start font-light" data-id="element-1780">
                        <span className="flex-shrink-0 mr-3 mt-1.5" data-id="element-1781">
                          <span className="block w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.5)]" data-id="element-1782"></span>
                        </span>
                        <span data-id="element-1783">{feature}</span>
                      </li>)}
                  </ul>
                  {/* Limitations */}
                  {ticket.limitations && <div className="mb-6 border border-amber-400/20 p-4 bg-black/40" data-id="element-1784">
                      <div className="flex items-center mb-2" data-id="element-1785">
                        <AlertCircle className="w-4 h-4 text-amber-400 mr-2" data-id="element-1786" />
                        <span className="text-amber-400 text-xs uppercase tracking-wider" data-id="element-1787">
                          Limitations
                        </span>
                      </div>
                      <ul className="space-y-1" data-id="element-1788">
                        {ticket.limitations.map((limitation, i) => <li key={i} className="text-white/50 text-xs flex items-start font-light" data-id="element-1789">
                            <span className="mr-2" data-id="element-1790">•</span>
                            <span data-id="element-1791">{limitation}</span>
                          </li>)}
                      </ul>
                    </div>}
                  {/* CTA Button */}
                  <motion.a href={ticket.link} target="_blank" rel="noopener noreferrer" className={`w-full py-3 text-center transition-colors duration-300 ${ticket.popular ? 'bg-amber-400 hover:bg-amber-500 text-black font-medium' : 'border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white'}`} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-1792">
                    <span className="flex items-center justify-center gap-2 text-sm tracking-wider uppercase" data-id="element-1793">
                      {ticket.popular && <Sparkles className="w-4 h-4" data-id="element-1794" />}
                      Buy Tickets Now
                    </span>
                  </motion.a>
                </div>
              </div>
            </motion.div>)}
        </div>
        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center max-w-4xl mx-auto" data-id="element-1795">
          <h3 className="font-serif text-3xl md:text-4xl text-white mb-4" data-id="element-1796">
            History will only be reborn{' '}
            <span className="text-amber-400" data-id="element-1797">once</span>. Will you be there?
          </h3>
          <p className="text-xl text-white/70 mb-8 font-light" data-id="element-1798">
            Tickets are selling fast. Reserve your place in history today.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-1799">
            <motion.a href="https://www.tickettailor.com/events/sikhkingdoms/1934316" target="_blank" rel="noopener noreferrer" className="bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1800">
              Buy Tickets Now
            </motion.a>
            <motion.a href="https://www.tickettailor.com/events/sikhkingdoms/1934316" target="_blank" rel="noopener noreferrer" className="border-2 border-amber-400/50 hover:bg-amber-400 hover:text-black text-white px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1801">
              View All Ticket Options
            </motion.a>
          </div>
        </motion.div>
        {/* Private & Partnership Events Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-6xl mx-auto mt-20" data-id="element-1802">
          <div className="text-center mb-12" data-id="element-1803">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-4" data-id="element-1804">
              Exclusive Partnerships
            </p>
            <h3 className="font-serif text-4xl md:text-5xl text-white mb-4" data-id="element-1805">
              PRIVATE &amp; <span className="text-amber-400" data-id="element-1806">PARTNERSHIP</span>{' '}
              EVENTS
            </h3>
            <div className="w-24 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-1807"></div>
            <p className="text-white/70 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-1808">
              Transform the Sikh Kingdoms experience into a bespoke event for
              your organization, community, or constituency. We offer tailored
              partnerships for corporate sponsors, creators, and community
              leaders.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-1809">
            {/* Corporate Sponsors */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0
          }} data-id="element-1810">
              <Link to="/corporate" className="group block h-full" data-id="element-1811">
                <div className="border-2 border-amber-400/30 group-hover:border-amber-400 bg-black/60 p-8 h-full transition-all duration-300 relative overflow-hidden" data-id="element-1812">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/5 group-hover:bg-amber-400/10 transition-colors duration-300 rounded-bl-full" data-id="element-1813"></div>
                  <div className="w-12 h-12 flex items-center justify-center border border-amber-400/30 group-hover:border-amber-400 mb-6 transition-colors duration-300" data-id="element-1814">
                    <Building2 className="w-6 h-6 text-amber-400" data-id="element-1815" />
                  </div>
                  <p className="text-amber-400/70 text-xs tracking-[0.2em] uppercase mb-2 font-light" data-id="element-1816">
                    Brand Partnership &amp; Sponsorship
                  </p>
                  <h4 className="font-serif text-2xl text-white mb-3 group-hover:text-amber-400 transition-colors duration-300" data-id="element-1817">
                    Corporate Sponsors
                  </h4>
                  <p className="text-white/60 text-sm font-light leading-relaxed mb-6" data-id="element-1818">
                    Transform your next corporate gathering into an
                    unforgettable cultural experience. Host your team, clients,
                    or partners for an exclusive private evening in the Sikh
                    Kingdoms.
                  </p>
                  <div className="flex items-center text-amber-400 text-sm tracking-wider uppercase font-light" data-id="element-1819">
                    <span data-id="element-1820">Explore Partnership</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" data-id="element-1821" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Influencers & Creators */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-1822">
              <Link to="/influencers" className="group block h-full" data-id="element-1823">
                <div className="border-2 border-amber-400/30 group-hover:border-amber-400 bg-black/60 p-8 h-full transition-all duration-300 relative overflow-hidden" data-id="element-1824">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/5 group-hover:bg-amber-400/10 transition-colors duration-300 rounded-bl-full" data-id="element-1825"></div>
                  <div className="w-12 h-12 flex items-center justify-center border border-amber-400/30 group-hover:border-amber-400 mb-6 transition-colors duration-300" data-id="element-1826">
                    <Sparkles className="w-6 h-6 text-amber-400" data-id="element-1827" />
                  </div>
                  <p className="text-amber-400/70 text-xs tracking-[0.2em] uppercase mb-2 font-light" data-id="element-1828">
                    Host an Exclusive Experience
                  </p>
                  <h4 className="font-serif text-2xl text-white mb-3 group-hover:text-amber-400 transition-colors duration-300" data-id="element-1829">
                    Influencers &amp; Creators
                  </h4>
                  <p className="text-white/60 text-sm font-light leading-relaxed mb-6" data-id="element-1830">
                    Create extraordinary content at the intersection of culture,
                    cuisine, and immersive art. Bring your audience into a world
                    of 360° projections and royal Punjabi heritage.
                  </p>
                  <div className="flex items-center text-amber-400 text-sm tracking-wider uppercase font-light" data-id="element-1831">
                    <span data-id="element-1832">Explore Hosting</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" data-id="element-1833" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Politicians & Community Leaders */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1834">
              <Link to="/politicians" className="group block h-full" data-id="element-1835">
                <div className="border-2 border-amber-400/30 group-hover:border-amber-400 bg-black/60 p-8 h-full transition-all duration-300 relative overflow-hidden" data-id="element-1836">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/5 group-hover:bg-amber-400/10 transition-colors duration-300 rounded-bl-full" data-id="element-1837"></div>
                  <div className="w-12 h-12 flex items-center justify-center border border-amber-400/30 group-hover:border-amber-400 mb-6 transition-colors duration-300" data-id="element-1838">
                    <Landmark className="w-6 h-6 text-amber-400" data-id="element-1839" />
                  </div>
                  <p className="text-amber-400/70 text-xs tracking-[0.2em] uppercase mb-2 font-light" data-id="element-1840">
                    Constituency &amp; Cultural Night
                  </p>
                  <h4 className="font-serif text-2xl text-white mb-3 group-hover:text-amber-400 transition-colors duration-300" data-id="element-1841">
                    Elected Officials &amp; Community Leaders
                  </h4>
                  <p className="text-white/60 text-sm font-light leading-relaxed mb-6" data-id="element-1842">
                    Host a nonpartisan constituency night that celebrates
                    heritage, education, and multicultural excellence — a
                    community economic win for your riding.
                  </p>
                  <div className="flex items-center text-amber-400 text-sm tracking-wider uppercase font-light" data-id="element-1843">
                    <span data-id="element-1844">Explore Constituency Night</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" data-id="element-1845" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>

          {/* General Inquiry CTA */}
          <div className="text-center" data-id="element-1846">
            <p className="text-white/50 text-sm font-light mb-4" data-id="element-1847">
              Not sure which option is right for you?
            </p>
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20a%20private%20event%20or%20partnership%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 border-2 border-amber-400/50 hover:border-amber-400 hover:bg-amber-400 hover:text-black text-white px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1848">
              <MessageCircle className="w-4 h-4" data-id="element-1849" />
              General Partnership Inquiry
            </motion.a>
          </div>
        </motion.div>
      </div>
    </motion.section>;
};